'use client'

// Components
import { Button, BUTTON_TYPES, Image, Link } from '@src/quince-components/src/atoms'
import ImageDomain from '@src/quince-site-common/src/domains/Image'
import PartnerProduct from '@src/quince-site-common/src/domains/PartnerProduct'
import { useGotoPartnerSite } from '@src/quince-site-common/src/hooks/useGotoPartnerSite'
import usePx from '@src/quince-site-common/src/hooks/usePx'
import partnerModalStateManager from '@src/quince-site-common/src/redux/globalStateManager/PartnerModalStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { getStringFromSlugInput } from '@src/quince-site-common/src/utils/getURLStringFromInputString'
import { SectionProps } from '@src/SectionRenderer/SectionRenderer.types'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackClickEvent, trackEvent } from '@src/telemetry/track'
import { usePathname } from 'next/navigation'
import { useTranslation } from 'next-i18next'
import { useEffect } from 'react'

import { renderDiscount, renderTermsAndCondition } from '../../partnerPDPLandingPage.helper'

import styles from './partnerPDPLandingForm.module.scss'

const PartnerPdpLandingForm = (props: SectionProps) => {
  const { t } = useTranslation('common')
  const { partnerProduct } = props.sectionConfig
  const slug = partnerProduct?.slug.split('/').filter(x => x)
  const { openPartnerSite } = useGotoPartnerSite()
  const openEmailInputModal = useAppSelector(partnerModalStateManager, partnerModalStateManager.getOpenEmailInputModal)

  const gotoPartnerSite = async () => {
    trackClickEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
      action: 'click_start_earning',
      context: {
        title: partnerProduct?.title,
        // @i18n-ignore
        source: 'PDP',
        merchantId: partnerProduct?.merchantId,
      },
    })
    partnerModalStateManager.setPartnerProduct(partnerProduct)
    const emailId = AuthUtil.getUser().email
    if (emailId) {
      await openPartnerSite(emailId, partnerProduct?.partnerSiteUrl, partnerProduct?.title, openEmailInputModal, 'PDP')
    } else {
      window.location.hash = '#email-input-modal'
      trackEvent(TELEMETRY_EVENT_NAMES.FORM_OPEN, {
        action: 'email_partnership_offers_open',
        context: {
          title: partnerProduct?.title,
          // @i18n-ignore
          source: 'PDP',
        },
      })
      partnerModalStateManager.setOpenEmailInputModal(true)
      partnerModalStateManager.setSource('PDP')
    }
  }
  const pathname = usePathname()
  useEffect(() => {
    partnerModalStateManager.setOpenEmailInputModal(false)
  }, [pathname])

  const getPxFromRem = usePx()
  const offset = getPxFromRem(6, false)
  useEffect(() => {
    if (window.location.hash) {
      const hashParam = window.location.hash.split('?')[0]
      const hash = hashParam.slice(1, hashParam.length)
      // Added setTimeout because of hyderation. when we try to scroll to particular hash, Some of the
      // components might not be filled/ hyderated at that instance of time. which creates issue of improper scolling
      // Delay provide buffer time for hyderation.
      setTimeout(() => {
        scrollToHowItWorksSection(hash, offset)
      }, 1000)
    }
  }, [offset])

  const showImage = !!partnerProduct?.partnerProductImages[0]

  return (
    <div>
      <div className={styles.ProductPdpcontainer}>
        <div className={styles.breadcrum}>
          <Link className={styles.grayed} href={`/${slug ? slug[0] : ''}`}>
            {slug ? getStringFromSlugInput(slug[0]) : ''}
          </Link>
          {slug?.length > 0 ? ` / ${slug[1]}` : ''}
        </div>
        <div className={styles.sectionWrapper}>
          <div>{showImage && <Image {...makeImageProps(partnerProduct)} className={styles.productImage} />}</div>
          <div className={styles.productInfo}>
            <div className={styles.title}>{partnerProduct?.title}</div>
            <div className={styles.subtitle}>
              {partnerProduct?.subtitle}
              <div className={styles.subtitleSubscript}>{t('in_quince_credit')}</div>
            </div>
            <div className={styles.howitworksVerbiage}>
              {partnerProduct?.subDescription && <div>{partnerProduct.subDescription}</div>}{' '}
            </div>
            {partnerProduct?.discount && (
              <div className={styles.offerMessage}>
                {renderDiscount(partnerProduct)}
                <span
                  className={styles.link}
                  onClick={() => {
                    scrollToHowItWorksSection('how-it-works', offset)
                  }}
                >
                  {' '}
                  {t('var1_v1', { var1: t('here_s_how_it_works') })}
                </span>
              </div>
            )}
            <div className={styles.cta}>
              <Button type={BUTTON_TYPES.PRIMARY} onClick={() => void gotoPartnerSite()} className={styles.button}>
                {t('start_earning')}
              </Button>
            </div>
            {/* {partnerProduct?.description && (
               <div className={styles.description}>{partnerProduct?.description}</div>
              )} */}
            <div className={styles.terms}>
              {renderTermsAndCondition(partnerProduct)}{' '}
              <Link className={styles.termsLink} href="/terms">
                {t('terms_apply')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const scrollToHowItWorksSection = (hash: string, offset: number) => {
  const element = document.querySelector(`[data-bring-in-view=${hash}]`)
  if (element) {
    const elementPosition = element.getBoundingClientRect().top
    const offsetPosition = elementPosition + window.pageYOffset - offset
    window.scrollTo({
      top: offsetPosition,
      // @i18n-ignore
      behavior: 'smooth',
    })
    window.location.hash = `#how-it-works`
  }
}

const makeImageProps = (partnerProduct: PartnerProduct) => {
  return ImageDomain.fromContentful(partnerProduct.partnerProductImages[0])
}

export default PartnerPdpLandingForm
